import React, { useEffect, useState } from 'react'

import {
  Typography,
  Box,
  Paper,
  FormControlLabel,
  Switch,
  Button,
} from '@material-ui/core'
import { FormContext, useForm } from 'react-hook-form'
import { Alert, AlertTitle } from '@material-ui/lab'

import { LoadingFeedback, Permitted, RiskAssumedInfo } from 'components'
import { Form, RiskAssumedDialog } from './components'

import useIncidentManegement from 'hooks/useIncidentManegement'
import useFetch from 'hooks/useFetch'
import useSnackbar from 'hooks/useSnackbar'

import formatters from 'helpers/formatters'

import * as service from 'service'
import constants from 'constants/index'
import permissions from 'constants/permissions'

const CommunicationEmailStep = ({ refresh }) => {
  const {
    incident,
    alreadySentCommunicationEmail,
    isShow,
    handleNext,
    wantsToAdvanceStep,
    resetAdvanceStep,
  } = useIncidentManegement()

  const snackbar = useSnackbar()

  const [showForm, setShowForm] = useState(incident?.shouldNotify || false)
  const [openRiskDialog, setOpenRiskDialog] = useState(false)

  const { response, isLoading } = useFetch(
    service.dponet.communicationIncidentEmail.show,
    { incidentId: incident?.id },
  )

  const communicationIncidentEmail =
    response?.data?.communicationIncidentEmail || {}

  const formMethods = useForm({
    mode: 'onChange',
    defaultValues: {
      attachment: undefined,
    },
  })

  const { watch } = formMethods

  const attachment = watch('attachment')

  const incidentTest =
    incident?.incidentType === constants.documents.TEST_INCIDENT_VALUE

  const isRiskAssumed = incident.riskAssumptions?.communication?.riskAssumed

  const handleChangeShowForm = () => setShowForm((showForm) => !showForm)

  const handleOpenRiskDialog = () => setOpenRiskDialog(true)

  useEffect(() => {
    if (
      wantsToAdvanceStep &&
      (!incident?.shouldNotify || isRiskAssumed || incidentTest)
    ) {
      const advance = async () => {
        try {
          await service.dponet.incidents.concludeStep({
            incidentId: incident?.id,
            step: constants.incidentManegement.COMMUNICATION_ENUM,
          })
        } catch (error) {
          snackbar.open({
            message: formatters.errorMessage(error),
            variant: 'error',
          })
        } finally {
          resetAdvanceStep()
          handleNext()
        }
      }
      advance()
    }
  }, [wantsToAdvanceStep])

  if (incidentTest) {
    return (
      <Alert variant="standard" severity="info">
        <AlertTitle>Fase de Comunicação</AlertTitle>
        Este é um incidente de teste. Etapas de tratamento e comunicação estão
        desabilitadas
      </Alert>
    )
  }

  return (
    <>
      <Box id={constants.incidents.INCIDENT_MAIN_DRIVER_STEP_10}>
        <LoadingFeedback open={isLoading} />
        {!incident?.shouldNotify && (
          <Paper component={Box} p={2} mb={2}>
            <Typography variant="body1" gutterBottom>
              O DPO responsável concluiu que não será necessário notificar os
              titulares de dados pois não houve dano e/ou risco relevante,
              portanto, esta etapa não será exigida. No entanto, se desejar
              notificar, basta ativá-la no botão abaixo.
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  defaultChecked={showForm}
                  color="primary"
                  onChange={handleChangeShowForm}
                  value={showForm}
                />
              }
              label="Ativar notificação dos titulares"
              labelPlacement="end"
              disabled={isShow}
            />
          </Paper>
        )}
        {showForm && (
          <>
            {isRiskAssumed ? (
              <RiskAssumedInfo
                incident={incident}
                isShow={isShow}
                refresh={refresh}
                step={constants.incidentRiskAssumption.COMMUNICATION_ENUM}
                successMessage="A comunicação aos titulares foi reativada com sucesso."
                message="O Risco foi assumido para não comunicar os titulares de dados, portanto, esta etapa não será exigida."
                buttonText="Comunicar Titulares"
                confirmMessage="Tem certeza que deseja cancelar o risco assumido e reativar a comunicação aos titulares?"
              />
            ) : (
              <>
                {!alreadySentCommunicationEmail ? (
                  <>
                    <Box mb={2}>
                      <Alert severity="warning" variant="outlined">
                        <Typography variant="body2">
                          Atenção! Para o envio dos e-mails, é necessário que a
                          configuração SMTP (servidor de e-mail) esteja
                          devidamente configurada no sistema. Caso contrário, o
                          envio não será realizado.
                        </Typography>
                      </Alert>
                    </Box>
                    <Box mb={2}>
                      <Alert severity="info" variant="outlined">
                        <Typography variant="body2">
                          Após anexar a lista de titulares de dados e os campos
                          de assunto e corpo estarem preenchidos, o sistema
                          estará apto a enviar os e-mails para os titulares de
                          dados. Após concluir o arquivo será deletado do
                          sistema.
                        </Typography>
                      </Alert>
                    </Box>
                  </>
                ) : (
                  <Box mb={2}>
                    <Alert severity="success" variant="outlined">
                      <Typography variant="body2">
                        Os e-mails já foram enviados para os titulares de dados.
                      </Typography>
                    </Alert>
                  </Box>
                )}
                {incident?.shouldNotify && (
                  <Permitted tag={permissions.INCIDENTS.MANAGE}>
                    <Paper
                      component={Box}
                      display="flex"
                      width="100%"
                      justifyContent="space-between"
                      alignItems="center"
                      p={2}
                      mb={2}
                    >
                      <Typography variant="subtitle1">
                        Assumir o Risco de não comunicar os titulares de dados
                        mesmo com a orientação do Encarregado de Dados
                      </Typography>
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={handleOpenRiskDialog}
                        disabled={isShow}
                      >
                        Assumir risco
                      </Button>
                    </Paper>
                  </Permitted>
                )}
                <FormContext {...formMethods}>
                  <Form
                    communicationIncidentEmail={communicationIncidentEmail}
                    attachment={attachment}
                  />
                </FormContext>
              </>
            )}
          </>
        )}
        <RiskAssumedDialog
          open={openRiskDialog}
          setOpen={setOpenRiskDialog}
          incident={incident}
          refresh={refresh}
          handleNext={handleNext}
        />
      </Box>
    </>
  )
}

export default CommunicationEmailStep
